// ============================================================================
// Command Center — Type Definitions & Mock Data
// ============================================================================

export type ProjectType = 'personal' | 'professional';
export type ProjectStatus = 'active' | 'on-hold' | 'completed' | 'archived';
export type TaskStatus = 'backlog' | 'todo' | 'in_progress' | 'in_review' | 'done';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';
export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';
export type MilestoneStatus = 'upcoming' | 'in_progress' | 'completed' | 'overdue';
export type InsightType = 'risk' | 'blocker' | 'suggestion' | 'achievement' | 'reminder';
export type InsightSeverity = 'info' | 'warning' | 'critical';
export type CCTab = 'today' | 'operations' | 'board' | 'timeline' | 'insights';

export interface Subtask {
    id: string;
    title: string;
    completed: boolean;
}

export interface Assignee {
    id: string;
    name: string;
    avatar: string;
}

export interface Milestone {
    id: string;
    projectId: string;
    name: string;
    targetDate: string;
    status: MilestoneStatus;
}

export interface ProjectTask {
    id: string;
    projectId: string;
    title: string;
    description: string;
    status: TaskStatus;
    priority: TaskPriority;
    assignee: Assignee | null;
    dueDate: string | null;
    tags: string[];
    subtasks: Subtask[];
    milestoneId: string | null;
    governanceRequired: boolean;
    estimatedHours: number | null;
    completedAt: string | null;
    createdAt: string;
}

export interface Project {
    id: string;
    name: string;
    description: string;
    type: ProjectType;
    category: string;
    status: ProjectStatus;
    color: string;
    icon: string;
    riskLevel: RiskLevel;
    startDate: string;
    targetDate: string;
    milestones: Milestone[];
    tags: string[];
    createdAt: string;
}

export interface ReplicaInsight {
    id: string;
    projectId: string;
    type: InsightType;
    severity: InsightSeverity;
    message: string;
    actionLabel?: string;
    relatedTaskId?: string;
    createdAt: string;
}

export const KANBAN_COLUMNS: { key: TaskStatus; label: string; color: string; dotColor: string }[] = [
    { key: 'backlog', label: 'Backlog', color: '#6B7280', dotColor: 'bg-gray-500' },
    { key: 'todo', label: 'To Do', color: '#3B82F6', dotColor: 'bg-blue-500' },
    { key: 'in_progress', label: 'In Progress', color: '#F59E0B', dotColor: 'bg-amber-500' },
    { key: 'in_review', label: 'In Review', color: '#8B5CF6', dotColor: 'bg-purple-500' },
    { key: 'done', label: 'Done', color: '#10B981', dotColor: 'bg-emerald-500' },
];

export const PRIORITY_CONFIG: Record<TaskPriority, { label: string; color: string; cssClass: string }> = {
    urgent: { label: 'Urgent', color: '#FF453A', cssClass: 'urgent' },
    high: { label: 'High', color: '#FF9F0A', cssClass: 'high' },
    medium: { label: 'Medium', color: '#0A84FF', cssClass: 'medium' },
    low: { label: 'Low', color: '#8E8E93', cssClass: 'low' },
};

export const PROJECT_COLORS: Record<string, { bg: string; border: string; text: string; gradient: string }> = {
    blue: { bg: 'rgba(59,130,246,0.08)', border: 'rgba(59,130,246,0.25)', text: '#3b82f6', gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)' },
    purple: { bg: 'rgba(139,92,246,0.08)', border: 'rgba(139,92,246,0.25)', text: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' },
    emerald: { bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.25)', text: '#10b981', gradient: 'linear-gradient(135deg, #10b981, #059669)' },
    amber: { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.25)', text: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b, #d97706)' },
    rose: { bg: 'rgba(244,63,94,0.08)', border: 'rgba(244,63,94,0.25)', text: '#f43f5e', gradient: 'linear-gradient(135deg, #f43f5e, #e11d48)' },
    indigo: { bg: 'rgba(99,102,241,0.08)', border: 'rgba(99,102,241,0.25)', text: '#6366f1', gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)' },
    teal: { bg: 'rgba(20,184,166,0.08)', border: 'rgba(20,184,166,0.25)', text: '#14b8a6', gradient: 'linear-gradient(135deg, #14b8a6, #0d9488)' },
    orange: { bg: 'rgba(249,115,22,0.08)', border: 'rgba(249,115,22,0.25)', text: '#f97316', gradient: 'linear-gradient(135deg, #f97316, #ea580c)' },
};
